import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { jobAPI } from '../../services/api';
import Header from '../Header';
import Footer from '../Footer';
import BottomNavigationBar from '../BottomNavigationBar';
import JobDetailPanel from './JobDetailPanel';
import './AppliedJobsPage.css';

const AppliedJobsPage = ({ 
  onHomeClick, 
  onCategoriesClick,
  onHowItWorksClick,
  onProfileClick, 
  onAccountSettingsClick, 
  onForumClick 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [applications, setApplications] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalApplications, setTotalApplications] = useState(0);
  const [jobTitleSearch, setJobTitleSearch] = useState('');
  const [hasUserInteracted, setHasUserInteracted] = useState(false); // Track if user has manually interacted (to prevent auto-select loop)
  const [hasMoreApplications, setHasMoreApplications] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  // Initial load and filter changes (no debounce)
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    fetchAppliedJobs(true); // Reset applications when user, filter changes
  }, [user, statusFilter]);

  // Debounced search effect (only for jobTitleSearch)
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    
    // Debounce the search to avoid too many requests
    const debounceTimer = setTimeout(() => {
      fetchAppliedJobs(true); // Reset applications when search changes
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(debounceTimer);
  }, [jobTitleSearch]);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle body scroll when modal is open
  useEffect(() => {
    if (showMobileModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileModal]);

  // Auto-select first job when applications are loaded and no job is selected
  // Only on initial load, not after user closes modal
  useEffect(() => {
    if (applications.length > 0 && !selectedJob && !loading && !hasUserInteracted) {
      const firstApplication = applications[0];
      if (firstApplication.job && firstApplication.job.id) {
        setSelectedJob(firstApplication.job);
        // On mobile, also open the modal
        if (isMobile) {
          setShowMobileModal(true);
        }
      }
    }
  }, [applications, selectedJob, loading, isMobile, hasUserInteracted]);

  const fetchAppliedJobs = async (reset = false, preserveSelection = false) => {
    if (!user) return;
    
    if (reset) {
      setLoading(true);
      setCurrentPage(1);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const pageToFetch = reset ? 1 : currentPage + 1;
      
      // Build query parameters for server-side pagination and filtering
      const params = {
        per_page: perPage,
        page: pageToFetch,
      };

      // Add status filter for server-side filtering
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Add job title search if provided
      if (jobTitleSearch.trim()) {
        params.job_title = jobTitleSearch.trim();
      }
      
      const response = await jobAPI.getMyApplications(params);
      if (response.success) {
        // Handle paginated response structure
        const paginatedData = response.data;
        let appsData = [];
        
        console.log('Applied Jobs API Response:', paginatedData); // Debug log
        
        if (paginatedData?.data && Array.isArray(paginatedData.data)) {
          appsData = paginatedData.data;
          const lastPage = paginatedData.last_page || 1;
          const total = paginatedData.total || 0;
          console.log('Setting pagination:', { lastPage, total, currentPage: paginatedData.current_page }); // Debug log
          setTotalPages(lastPage);
          setTotalApplications(total);
          setHasMoreApplications(pageToFetch < lastPage);
        } else if (Array.isArray(paginatedData)) {
          appsData = paginatedData;
          // If it's just an array, we don't have pagination info
          setTotalPages(1);
          setTotalApplications(appsData.length);
          setHasMoreApplications(false);
        } else {
          // Fallback: treat as single page
          appsData = [];
          setTotalPages(1);
          setTotalApplications(0);
          setHasMoreApplications(false);
        }
        
        // Append or replace applications based on reset flag
        if (reset) {
          setApplications(appsData);
          setCurrentPage(1);
        } else {
          setApplications(prev => [...prev, ...appsData]);
          setCurrentPage(pageToFetch);
        }
        
        // Auto-select the first job if no job is currently selected and we have applications
        // Only do this on initial load (not preserveSelection and user hasn't interacted)
        const allApplications = reset ? appsData : [...applications, ...appsData];
        if (reset && allApplications.length > 0 && !selectedJob && allApplications[0].job && !hasUserInteracted) {
          if (allApplications[0].job.id) {
            setSelectedJob(allApplications[0].job);
            // On mobile, also open the modal
            if (isMobile) {
              setShowMobileModal(true);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching applied jobs:', error);
      if (reset) {
        setApplications([]);
      }
      setTotalPages(1);
      setTotalApplications(0);
      setHasMoreApplications(false);
    } finally {
      // Only reset the loading state that was set
      if (reset) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMoreApplications) {
      fetchAppliedJobs(false, true);
    }
  };

  const handleJobSelect = (application, e) => {
    // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    
    if (isModifierPressed && application.job) {
      // Open job detail page in new tab
      e?.preventDefault();
      e?.stopPropagation();
      const job = application.job;
      // Use share_token if available, otherwise use ID
      const jobUrl = job.share_token 
        ? `/job/${job.share_token}`
        : `/vacancies/${job.id}`;
      window.open(jobUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // Prevent multiple clicks on the same item
    if (selectedJob?.id === application.job?.id && showMobileModal) {
      return; // Already selected and modal is open
    }

    console.log('Application clicked:', application); // Debug log
    setHasUserInteracted(true); // Mark that user has interacted
    
    if (application.job) {
      console.log('Selected job:', application.job); // Debug log
      // Ensure we have a valid job object with an ID
      if (application.job.id) {
        if (isMobile) {
          setSelectedJob(application.job);
          setShowMobileModal(true);
        } else {
          setSelectedJob(application.job);
        }
      } else {
        console.warn('Job object missing ID:', application.job);
      }
    } else {
      console.warn('Application has no job data:', application);
      // Try to fetch the job if we have job_id
      if (application.job_id) {
        fetchJobById(application.job_id);
      }
    }
  };

  const fetchJobById = async (jobId) => {
    try {
      const response = await jobAPI.get(jobId);
      if (response.success && response.data) {
        if (isMobile) {
          setSelectedJob(response.data);
          setShowMobileModal(true);
        } else {
          setSelectedJob(response.data);
        }
      }
    } catch (error) {
      console.error('Error fetching job:', error);
    }
  };

  const handleCloseModal = () => {
    setShowMobileModal(false);
    setSelectedJob(null);
    setHasUserInteracted(true); // Mark that user has closed modal to prevent auto-reopen
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'accepted':
        return 'status-accepted';
      case 'shortlisted':
        return 'status-shortlisted';
      case 'reviewed':
        return 'status-reviewed';
      case 'rejected':
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'accepted':
        return 'Accepted';
      case 'shortlisted':
        return 'Shortlisted';
      case 'reviewed':
        return 'Reviewed';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Pending';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="applied-jobs-page applied-jobs-with-bottom-nav">
      {/* Header - hidden on mobile */}
      <div className="applied-jobs-header-nav">
        <Header 
          onHomeClick={onHomeClick}
          onCategoriesClick={onCategoriesClick}
          onHowItWorksClick={onHowItWorksClick}
          onProfileClick={onProfileClick}
          onAccountSettingsClick={onAccountSettingsClick}
          onForumClick={onForumClick}
          onJobsClick={() => navigate('/vacancies')}
          isLoggedIn={isAuthenticated}
          user={user}
          currentPage="applied-jobs"
        />
      </div>

      <div className="applied-jobs-container">
        {/* Desktop: Sidebar with job list */}
        {!isMobile && (
          <div className="applied-jobs-sidebar">
            <div className="applied-jobs-header">
              <h1>My Applications</h1>
              <p className="subtitle">Track your job applications</p>
              <div className="applied-jobs-navigation-tabs">
                <button
                  className={`nav-tab`}
                  onClick={() => navigate('/my-jobs')}
                >
                  Posted Jobs
                </button>
                <button
                  className={`nav-tab active`}
                  onClick={() => navigate('/applied-jobs')}
                >
                  Applied Jobs
                </button>
              </div>
            </div>

            {/* Search by Job Title */}
            <div className="applied-jobs-search-section">
              <div className="applied-jobs-search-input-wrapper">
                <svg className="applied-jobs-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  className="applied-jobs-search-input"
                  placeholder="Search by job title..."
                  value={jobTitleSearch}
                  onChange={(e) => {
                    setJobTitleSearch(e.target.value);
                  }}
                />
                {jobTitleSearch && (
                  <button className="applied-jobs-search-clear" onClick={() => {
                    setJobTitleSearch('');
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Status Filter */}
            <div className="status-filter-section">
              <label>Filter by Status:</label>
              <select 
                value={statusFilter} 
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                }}
                className="status-filter-select"
              >
                <option value="all">All Applications</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Applications List */}
            <div className="applied-jobs-list">
              {loading ? (
                <div className="loading-state">Loading applications...</div>
              ) : applications.length === 0 ? (
                <div className="empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  <h3>No Applications Yet</h3>
                  <p>You haven't applied to any jobs yet. Start applying to see your applications here.</p>
                  <button 
                    className="browse-jobs-btn"
                    onClick={() => navigate('/vacancies')}
                  >
                    Browse Jobs
                  </button>
                </div>
              ) : (
                applications.map((application) => (
                  <div
                    key={application.id}
                    className={`applied-job-item ${selectedJob?.id === application.job?.id ? 'active' : ''}`}
                    onClick={(e) => handleJobSelect(application, e)}
                  >
                    <div className="job-item-header">
                      <h3>{application.job?.title || 'Job Title'}</h3>
                      <span className={`status-badge ${getStatusBadgeClass(application.status)}`}>
                        {getStatusLabel(application.status)}
                      </span>
                    </div>
                    <div className="job-item-meta">
                      <span className="job-item-company">{application.job?.company?.name || application.job?.postedBy?.name || 'Company'}</span>
                      {application.job?.work_type && (
                        <>
                          <span className="job-item-separator">•</span>
                          <span className="work-type">{application.job.work_type}</span>
                        </>
                      )}
                    </div>
                    <div className="job-item-footer">
                      <span className="applied-date">{formatDate(application.applied_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Load More Button - Desktop */}
            {hasMoreApplications && (
              <div className="applied-jobs-load-more">
                <button
                  className="load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loadingMore || loading}
                >
                  {loadingMore ? (
                    <>
                      <div className="load-more-spinner"></div>
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
                {totalApplications > 0 && (
                  <div className="load-more-info">
                    Showing {applications.length} of {totalApplications} applications
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Desktop: Job details panel */}
        {!isMobile && selectedJob && (
          <div className="applied-jobs-detail-panel">
            <JobDetailPanel 
              job={selectedJob}
              applications={[]}
              onJobUpdate={() => {}}
              isAppliedJobsView={true}
            />
          </div>
        )}

        {/* Desktop: Empty state when no job selected */}
        {!isMobile && !selectedJob && applications.length > 0 && (
          <div className="applied-jobs-detail-panel empty">
            <div className="empty-detail-state">
              <svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
              <h2>Select an Application</h2>
              <p>Click on an application from the list to view details</p>
            </div>
          </div>
        )}

        {/* Mobile: Applications list */}
        {isMobile && (
          <div className="applied-jobs-mobile-content">
            <div className="applied-jobs-mobile-header">
              <h1>My Applications</h1>
              {/* Navigation Tabs - Mobile */}
              <div className="applied-jobs-navigation-tabs-mobile">
                <button
                  className={`nav-tab-mobile ${location.pathname === '/my-jobs' ? 'active' : ''}`}
                  onClick={() => navigate('/my-jobs')}
                >
                  Posted Jobs
                </button>
                <button
                  className={`nav-tab-mobile ${location.pathname === '/applied-jobs' ? 'active' : ''}`}
                  onClick={() => navigate('/applied-jobs')}
                >
                  Applied Jobs
                </button>
              </div>
              {/* Search by Job Title - Mobile */}
              <div className="applied-jobs-search-section-mobile">
                <div className="applied-jobs-search-input-wrapper">
                  <svg className="applied-jobs-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <input
                    type="text"
                    className="applied-jobs-search-input"
                    placeholder="Search by job title..."
                    value={jobTitleSearch}
                    onChange={(e) => {
                      setJobTitleSearch(e.target.value);
                    }}
                  />
                  {jobTitleSearch && (
                    <button className="applied-jobs-search-clear" onClick={() => {
                      setJobTitleSearch('');
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <select 
                value={statusFilter} 
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                }}
                className="status-filter-select-mobile"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="applied-jobs-mobile-list">
              {loading ? (
                <div className="loading-state">Loading applications...</div>
              ) : applications.length === 0 ? (
                <div className="empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  <h3>No Applications Yet</h3>
                  <p>You haven't applied to any jobs yet. Start applying to see your applications here.</p>
                  <button 
                    className="browse-jobs-btn"
                    onClick={() => navigate('/vacancies')}
                  >
                    Browse Jobs
                  </button>
                </div>
              ) : (
                applications.map((application) => (
                  <div
                    key={application.id}
                    className="applied-job-item-mobile"
                    onClick={(e) => handleJobSelect(application, e)}
                  >
                    <div className="job-item-header">
                      <h3>{application.job?.title || 'Job Title'}</h3>
                      <span className={`status-badge ${getStatusBadgeClass(application.status)}`}>
                        {getStatusLabel(application.status)}
                      </span>
                    </div>
                    <div className="job-item-meta">
                      <span className="job-item-company">{application.job?.company?.name || application.job?.postedBy?.name || 'Company'}</span>
                      {application.job?.work_type && (
                        <>
                          <span className="job-item-separator">•</span>
                          <span className="work-type">{application.job.work_type}</span>
                        </>
                      )}
                    </div>
                    <div className="job-item-footer">
                      <span className="applied-date">{formatDate(application.applied_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Load More Button - Mobile */}
            {hasMoreApplications && (
              <div className="applied-jobs-load-more">
                <button
                  className="load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loadingMore || loading}
                >
                  {loadingMore ? (
                    <>
                      <div className="load-more-spinner"></div>
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
                {totalApplications > 0 && (
                  <div className="load-more-info">
                    Showing {applications.length} of {totalApplications} applications
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mobile: Job detail modal */}
        {isMobile && showMobileModal && selectedJob && (
          <div className="applied-job-detail-mobile-modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseModal();
            }
          }}>
            <div className="applied-job-detail-mobile-modal" onClick={(e) => e.stopPropagation()}>
              <div className="mobile-modal-header">
                <button className="mobile-modal-close" onClick={(e) => {
                  e.stopPropagation();
                  handleCloseModal();
                }} aria-label="Close">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="mobile-modal-content">
                <JobDetailPanel 
                  job={selectedJob}
                  applications={[]}
                  onJobUpdate={() => {}}
                  isAppliedJobsView={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer 
        onHomeClick={onHomeClick}
        onCategoriesClick={onCategoriesClick}
        onHowItWorksClick={onHowItWorksClick}
        onProfileClick={onProfileClick}
        onAccountSettingsClick={onAccountSettingsClick}
        onForumClick={onForumClick}
      />

      {/* Bottom Navigation - Mobile only */}
      {isMobile && (
        <BottomNavigationBar 
          onHomeClick={onHomeClick}
          onCategoriesClick={onCategoriesClick}
          onHowItWorksClick={onHowItWorksClick}
          onProfileClick={onProfileClick}
          onAccountSettingsClick={onAccountSettingsClick}
          onForumClick={onForumClick}
        />
      )}
    </div>
  );
};

export default AppliedJobsPage;

